// „Generálási kihagyások" fül tartalma — a számla-generátor kihagyás-naplója
// (`billing/invoice-generation-skips`, `GET /invoices/generation-skips`): mely hozzárendelés/tétel miért nem
// került számlára egy hónapban. A lista-mintát követi (a számla-lista a referencia): `ListToolbar` (hónap +
// ok + ügyfél + állapot + kereső) + csupasz `DataTable`, végtelen görgetés (100/lap). Page-gate: a hívó
// (`invoices.index.tsx`) már `invoices.view`-ra kapuzva rendereli ezt a komponenst.
import {
  INVOICE_GENERATION_SKIP_REASONS,
  type InvoiceGenerationSkipReason,
  type InvoiceGenerationSkipRowSchema,
} from '@atlas/contracts';
import { ArrowTopRightOnSquareIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander, type DetailsRow } from '@/components/shared/details-expander';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { RowActionButton, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import { Text } from '@/components/ui/text';
import { invoiceKeys } from '@/features/invoices/api';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { formatDay } from '@/lib/format-datetime';
import { formatName } from '@/lib/format-name';
import { distinctPlanName, servicePlanLabel } from '@/lib/service-plan-label';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type SkipRow = z.infer<typeof InvoiceGenerationSkipRowSchema>;
/**
 * A végtelen-görgetett napló cache-alakja az OPTIMISTA feloldáshoz — szándékosan LAZA (`body: unknown`),
 * mert a ts-rest lap-uniója `status === 200`-ra nem szűkül (mindkét ág `number` státuszt hirdet). A
 * frissítő ezért futásidőben ellenőrzi a `skips` tömböt, és érintetlenül hagy minden más lapot.
 */
interface InfiniteSkips {
  pages: { status: number; body: unknown }[];
  pageParams: unknown[];
}
const PAGE_SIZE = 100;
const ALL = '__all__';
/**
 * A NYITOTT / LEZÁRT tengely HÁROM állása. A `nyitott` a TÉNYLEGES alapértelmezés (a szűrő `allValue`-ja,
 * így nem számít aktív szűrőnek) — a napló elsődleges olvasata a TEENDŐ-lista, nem a történet.
 */
const STATE_OPEN = 'open';
const STATE_RESOLVED = 'resolved';
const STATE_ALL = 'all';

/**
 * ISO → lokalizált dátum ÉS óra, KÜLÖN — a cella két sorban mutatja őket.
 *
 * MIÉRT NEM EGY SORBAN: a `toLocaleString` teljes alakja (dátum + óra) a hasáb szélességén MINDEN soron
 * csonkolt volt (mérve 1280 px-en), méghozzá épp az órát vágta le. Két sorban a dátum teljes, az óra pedig
 * másodlagos adatként, halványan áll alatta — ez a lista dátum-konvenciója.
 */
function fmtDateTime(iso: string, locale: string): { date: string; time: string } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString(locale),
    time: d.toLocaleTimeString(locale, { timeStyle: 'short' }),
  };
}
function fmtDate(iso: string, locale: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale);
}

/**
 * A `details` payload EGY mezőjének LOKALIZÁLT értéke.
 *
 * A napló NYERS technikai kulcsokat tárol (`pricingType: 'base_tiered'`, `usageSourceType: 'rs_direct'`), a
 * felületen viszont a rendszer MEGLÉVŐ szótára kell — pontosan az a címke, amit a hozzárendelés-űrlap és a
 * számla-lista is mutat. Enélkül ugyanaz a fogalom két néven élne a felületen, és a napló kódfejtést kérne.
 */
function detailValueLabel(key: string, value: unknown, t: TFunction): string {
  if (value === null || value === undefined || value === '') return t('invoices.skips.detailNone');
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  switch (key) {
    case 'pricingType':
      return t(`serviceAssignments.pricingTypeLabel.${raw}`, { defaultValue: raw });
    case 'usageSourceType':
      return t(`usageData.sourceType.${raw}`, { defaultValue: raw });
    case 'vatRate':
      return t(`serviceAssignments.vatRateLabel.${raw}`, { defaultValue: raw });
    case 'issuerKey':
      return t(`invoices.issuerLabel.${raw}`, { defaultValue: raw });
    case 'level':
      return t(`invoices.skips.autoBillingLevel.${raw}`, { defaultValue: raw });
    case 'assignmentStatus':
      return t(`serviceAssignments.statusLabel.${raw}`, { defaultValue: raw });
    case 'idField':
      return t(`invoices.skips.refField.${raw}`, { defaultValue: raw });
    default:
      return raw;
  }
}

/**
 * AZ OK EMBERI MAGYARÁZATA — egy mondat arról, MI történt és MIT kell tenni, a sor saját `details`-éből
 * kitöltve (deviza, hónap, árazási típus, kibocsátó-kulcs …).
 *
 * MIÉRT NEM ELÉG A JELVÉNY: a rövid ok-címke („Nincs használati adat") megmondja a KATEGÓRIÁT, de nem azt,
 * hogy melyik forrásról és melyik hónapról van szó, és főleg nem azt, hogy a kollégának hol kell javítania.
 * A napló attól lesz TEENDŐ-lista, hogy ez a mondat ott van a soron (user-lelet).
 *
 * Hiányzó `details`-mező → lokalizált helykitöltő (`—` / „ismeretlen"), sosem `undefined` a mondatban.
 */
function reasonDescription(r: SkipRow, t: TFunction): string {
  const d = r.details ?? {};
  const val = (key: string): string =>
    key in d ? detailValueLabel(key, d[key], t) : t('invoices.skips.detailUnknown');
  return t(`invoices.skips.reasonDesc.${r.reason}`, {
    pricingType: val('pricingType'),
    // UGYANAZ A CÍMKE, MONDAT KÖZEPÉRE. A `no_price` magyarázatában az árazási típus nem a mondat elején
    // áll („…a(z) alapdíj + sávos árazáshoz…"), ott a nagy kezdőbetű idegen. A négy árazási címke tiszta
    // köznév (nincs köztük márkanév), tehát a kisbetűsítés biztonságos. SIMA `toLowerCase`, nem locale-os:
    // a lap két nyelve (hu/en) közt nincs eltérő kisbetűsítési szabály, egy bedrótozott locale viszont a
    // másik nyelven lenne rossz.
    pricingTypeLower: val('pricingType').toLowerCase(),
    usageSourceType: val('usageSourceType'),
    currency: val('currency'),
    rateMonth: val('rateMonth'),
    vatRate: val('vatRate'),
    issuerKey: val('issuerKey'),
    // A KÉZI TÖRLÉS ZÁRÁNÁL: melyik piszkozatról tűnt el a sor.
    invoiceNumber: val('invoiceNumber'),
    status: val('status'),
    // A HOZZÁRENDELÉS saját státusza (`assignment_not_active`) — külön kulcs a HTTP-`status`-tól.
    assignmentStatus: val('assignmentStatus'),
    targetCurrency: val('targetCurrency'),
    usageCurrencies: val('usageCurrencies'),
    // HOL van kikapcsolva az automata számlázás (ügyfél vagy fiók) — enélkül a kolléga találgatna, melyik
    // kapcsolót keresse.
    level: val('level'),
    // A „nincs használati adat" pontosított okai: melyik hónapra és melyik azonosító hiányzik.
    usageMonth: val('usageMonth'),
    idField: val('idField'),
    defaultValue: '',
  });
}

/**
 * A TÉTEL-KULCS olvasható alakja. A zár-sor kulcsa a PERIÓDUS (`2026-09-01|2026-09-30`) — ez technikai kulcs,
 * a felületen nyelv szerinti dátum-tartomány kell (a közös `formatDay`-en, mint minden más dátum). Ismeretlen
 * alakú kulcs változatlanul marad: abból nem találunk ki dátumot.
 */
const PERIOD_KEY = /^(\d{4}-\d{2}-\d{2})\|(\d{4}-\d{2}-\d{2})$/;
function itemKeyLabel(itemKey: string, locale: string): string {
  const m = PERIOD_KEY.exec(itemKey);
  if (m === null) return itemKey;
  return `${formatDay(m[1], locale)} – ${formatDay(m[2], locale)}`;
}

/**
 * A „Részletek" kibontó sorai: a `details` payload lokalizálva, UTÁNA a két kivezetett oszlop (tétel ·
 * indító) és a futás-azonosító.
 *
 * MIÉRT KERÜLT IDE A TÉTEL ÉS AZ INDÍTÓ: külön hasábot egyik sem érdemel. A MAI generátor minden
 * hozzárendelést PONTOSAN EGY tételre képez (`SkipInfo.itemKey` doc-comment), tehát a „Tétel" oszlop MINDEN
 * soron ugyanazt írta („teljes számla") — nulla információ egy teljes hasábért. Az indító (ütemezett/kézi)
 * pedig támogatási adat, nem döntési: a teendőt nem az dönti el, ki indította a futást (user-döntés).
 */
function detailsRows(r: SkipRow, t: TFunction, locale: string): DetailsRow[] {
  const rows: DetailsRow[] = Object.entries(r.details ?? {}).map(([key, value]) => ({
    key,
    label: t(`invoices.skips.detailLabel.${key}`, { defaultValue: key }),
    // AZ ÁRAZÁSI TÍPUS KISBETŰS a kibontóban: itt nem CÍMKE (jelvény/select-érték), hanem az „Árazás: …"
    // mondat folytatása. CSS-sel megy, hogy az i18n-kulcs EGY maradjon — a hozzárendelés-űrlapon ugyanaz a
    // szöveg nagybetűs. A TÖBBI mezőre SZÁNDÉKOSAN nem áll: a használat-forrás és a kibocsátó MÁRKANÉV
    // („RoomSome Loyalty"), a devizakód pedig csupa nagybetű — azokat a kisbetűsítés elrontaná.
    value:
      key === 'pricingType' ? (
        <span className="lowercase">{detailValueLabel(key, value, t)}</span>
      ) : (
        detailValueLabel(key, value, t)
      ),
  }));
  rows.push({
    key: 'item',
    label: t('invoices.skips.columns.item'),
    value:
      r.itemKey === null ? t('invoices.skips.itemWholeInvoice') : itemKeyLabel(r.itemKey, locale),
  });
  rows.push({
    key: 'trigger',
    label: t('invoices.skips.columns.trigger'),
    value:
      r.trigger === 'cron'
        ? t('invoices.skips.triggerCron')
        : r.actorName === null && r.actorGivenName == null
          ? t('invoices.skips.triggerManual')
          : // NYELV SZERINTI névsorrend (magyarul vezetéknév elöl) — a közös formázón. `== null`: a mező a
            // telepítési ablakban (régebbi BFF) hiányozhat, ilyenkor az összefűzött név a fallback.
            formatName(
              {
                givenName: r.actorGivenName,
                familyName: r.actorFamilyName,
                displayName: r.actorName,
              },
              locale,
            ),
  });
  // A FUTÁS-AZONOSÍTÓ: ezzel fűzhető össze a napló-sor a generálás-futással (támogatási nyom).
  rows.push({ key: 'runId', label: t('invoices.skips.runIdLabel'), value: r.runId });
  return rows;
}

export function GenerationSkipsTable({
  month,
  onMonthChange,
}: {
  /**
   * A megjelenített hónap — a LAP közös dimenziója (a számla-fül ugyanerre néz), ezért a héj adja, nem
   * ez a komponens birtokolja. Korábban saját `skipMonth` kulcsa volt, és a két fül két külön hónapot
   * mutathatott ugyanazon az URL-en.
   */
  month: string;
  onMonthChange: (month: string) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can('invoices.manage');
  const locale = i18n.language;

  // A search-paramok `skip`-előtaggal — a MEGOSZTOTT `/invoices` URL-en a számla-fül saját kulcsai (`q`,
  // `status`, …) mellett futnak, ütközés nélkül.
  const { filters: urlFilters, setFilters } = useListFilters({
    skipReason: ALL,
    skipClientId: ALL,
    skipState: STATE_OPEN,
    skipQ: '',
  });
  const reason = urlFilters.skipReason;
  const clientId = urlFilters.skipClientId;
  const state = urlFilters.skipState;
  const q = urlFilters.skipQ;
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ skipQ: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // ÜGYFÉL-szűrő opciói — a számla-szerkesztő MEGLÉVŐ refs-forrása (nincs második igazság-forrás), de az
  // `invoiceRefs` `invoices.manage`-re kapuzott, ezért a facet CSAK managereknek jelenik meg (a revenue-forecast
  // szűrő-mintája, `showRefsFilters`).
  const refsQ = api.invoiceRefs.useQuery(
    ['invoices', 'refs', 'skips-filter'],
    { query: {} },
    { enabled: canManage, queryKey: ['invoices', 'refs', 'skips-filter'] },
  );
  const clientOptions = useMemo(
    () =>
      (refsQ.data?.status === 200 ? refsQ.data.body.clients : [])
        .map((c) => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label, locale)),
    [refsQ.data, locale],
  );

  const filters = {
    month,
    reason,
    clientId: canManage ? clientId : ALL,
    q,
    state,
  };
  const listKey = invoiceKeys.generationSkips(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) =>
      api.invoiceGenerationSkips.query({
        query: {
          month,
          reason: reason === ALL ? undefined : (reason as InvoiceGenerationSkipReason),
          clientId: canManage && clientId !== ALL ? clientId : undefined,
          q: q || undefined,
          state: state === STATE_OPEN ? undefined : (state as 'resolved' | 'all'),
          sort: 'occurredAt',
          order: 'desc',
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(() => pages.flatMap((p) => (p.status === 200 ? p.body.skips : [])), [pages]);
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = infinite.isError || hasNoSuccess;
  const isForbidden = pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '' || reason !== ALL || clientId !== ALL || state !== STATE_ALL;

  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  /**
   * A KÉZI TÖRLÉS ZÁRÁNAK FELOLDÁSA. Optimista: a sor AZONNAL eltűnik a nyitott listából (a zár lezárttá
   * válik), hiba esetén visszagördül — ugyanaz a szerződés, mint minden más mutációnál.
   */
  const release = useEntityMutation<{ id: string }, InfiniteSkips>({
    mutationFn: ({ id }) => api.invoiceGenerationSkipRelease.mutation({ params: { id }, body: {} }),
    invalidateKeys: [listKey],
    optimistic: {
      queryKey: listKey,
      update: (previous, vars) => {
        if (previous === undefined) return undefined;
        return {
          ...previous,
          pages: previous.pages.map((page) => {
            const body = page.body as { skips?: SkipRow[] } | null;
            if (page.status !== 200 || !Array.isArray(body?.skips)) return page;
            return {
              ...page,
              body: { ...body, skips: body.skips.filter((row) => row.id !== vars.id) },
            };
          }),
        };
      },
    },
    messages: {
      success: t('invoices.skips.releaseSuccess'),
      conflict: t('invoices.skips.releaseConflict'),
      forbidden: t('toast:invoices.forbidden'),
      validation: t('toast:invoices.validation'),
      error: t('toast:invoices.error'),
    },
  });
  const emDash = <span className="text-muted-foreground">—</span>;

  const columns: DataTableColumn<SkipRow>[] = [
    {
      // 1. ÜGYFÉL / FIÓK — a sor anchorja (`itemTitle`, mint minden lista-oldal első oszlopában), alatta a
      //    KÖZÖS „Részletek" kibontó. A kibontó a rendszer-konvenció szerint AZ ELSŐ oszlopba tartozik
      //    (`DetailsExpander` doc-comment) — a lista végén ülve a sor a jobb szélén kérte a magyarázatot,
      //    miközben a kérdés („melyik sor ez?") a bal szélen indul.
      id: 'client',
      header: t('invoices.skips.columns.client'),
      cardLabel: t('invoices.skips.columns.client'),
      card: 'title',
      width: 'minmax(0,1.6fr)',
      multiline: true,
      exportValue: (r) => [r.clientName, r.accountName].filter(Boolean).join(' / '),
      // MOBIL: a kibontó tartalma a kártya EGYETLEN „Részletek" rácsába megy (nem második kapcsolóként) —
      // ugyanaz a szerződés, mint a kiadás- és az eszköz-listán.
      cardExtraItems: (r) => detailsRows(r, t, locale),
      cell: (r) => (
        <div className="min-w-0 space-y-1">
          {r.clientName || r.accountName ? (
            <>
              <Text variant="itemTitle" as="div" className="truncate">
                {r.clientName ?? r.accountName}
              </Text>
              {r.clientName && r.accountName && (
                <Text tone="tertiary" as="div" className="truncate">
                  {r.accountName}
                </Text>
              )}
            </>
          ) : (
            emDash
          )}
          <DetailsExpander rows={detailsRows(r, t, locale)} />
        </div>
      ),
    },
    {
      // 2. SZOLGÁLTATÁS — név + csomag (csak ha ELTÉR). A 2. oszloptól halvány az alap-betű, a második sor
      //    `tertiary` — ugyanaz a hierarchia, mint a számla- és a kiadás-listán (rendszer-konvenció).
      id: 'service',
      header: t('invoices.skips.columns.service'),
      cardLabel: t('invoices.skips.columns.service'),
      width: 'minmax(0,1.5fr)',
      className: 'text-muted-foreground',
      multiline: true,
      exportValue: (r) => servicePlanLabel(r.serviceName, r.planName, ' / ') ?? '',
      cell: (r) =>
        r.serviceName || r.planName ? (
          <div className="min-w-0 space-y-0.5">
            {r.serviceName && <div className="truncate">{r.serviceName}</div>}
            {/* A csomag CSAK ha ELTÉR a szolgáltatás nevétől — egyszerű szerkezetnél a kettő azonos. */}
            {distinctPlanName(r.serviceName, r.planName) !== null && (
              <Text tone="tertiary" as="div" className="truncate">
                {r.planName}
              </Text>
            )}
          </div>
        ) : (
          emDash
        ),
    },
    {
      // 3. OK — a jelvény (kategória) ALATT a sor saját, kitöltött MAGYARÁZATA: mi történt és hol kell
      //    javítani. Ez a lista lényege, ezért a legszélesebb hasáb.
      id: 'reason',
      header: t('invoices.skips.columns.reason'),
      cardLabel: t('invoices.skips.columns.reason'),
      // A MOBIL-KÁRTYA badge-e az OK (nem az állapot): a lista alapból a NYITOTT kivételeket mutatja, tehát a
      // „nyitott" jelvény ott nem hordoz információt — az ok viszont a lista LÉNYEGE. (Mérve 375 px-en: a
      // kártya-fejlécben csak EGY badge fér el a cím mellett.)
      card: 'status',
      // A fejlécbe CSAK a jelvény megy; a magyarázat a kártya „Részletek" blokkjába, teljes szélességben
      // (`cardExtra`) — a kártya-fejlécben egy két-mondatos szöveg szétnyomná a címet.
      cardCell: (r) => (
        <DomainBadge
          kind="generationSkipReason"
          value={r.reason}
          label={t(`invoices.generate.skipReason.${r.reason}`, { defaultValue: r.reason })}
        />
      ),
      cardExtra: (r) => (
        <Text tone="tertiary" as="p" className="text-xs">
          {reasonDescription(r, t)}
        </Text>
      ),
      width: 'minmax(0,2.6fr)',
      multiline: true,
      exportValue: (r) =>
        [
          t(`invoices.generate.skipReason.${r.reason}`, { defaultValue: r.reason }),
          reasonDescription(r, t),
        ]
          .filter(Boolean)
          .join(' — '),
      cell: (r) => {
        const desc = reasonDescription(r, t);
        return (
          <div className="min-w-0 space-y-1">
            <DomainBadge
              kind="generationSkipReason"
              value={r.reason}
              label={t(`invoices.generate.skipReason.${r.reason}`, { defaultValue: r.reason })}
            />
            {desc && (
              <Text tone="tertiary" as="p" className="text-xs">
                {desc}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      // 4. IDŐPONT — mikor naplózta a generátor. Másodlagos adat (halvány alap-betű), a mobil-kártyán a
      //    „Részletek" alatt.
      id: 'occurredAt',
      header: t('invoices.skips.columns.occurredAt'),
      cardLabel: t('invoices.skips.columns.occurredAt'),
      width: 'minmax(0,0.9fr)',
      className: 'text-muted-foreground',
      multiline: true,
      exportValue: (r) => r.occurredAt,
      exportType: 'datetime',
      cell: (r) => {
        const at = fmtDateTime(r.occurredAt, locale);
        if (at === null) return <span className="truncate">{r.occurredAt}</span>;
        return (
          <div className="min-w-0 space-y-0.5">
            <div className="truncate">{at.date}</div>
            <Text tone="tertiary" as="div" className="truncate">
              {at.time}
            </Text>
          </div>
        );
      },
    },
    {
      // 5. ÁLLAPOT — a lista UTOLSÓ oszlopa (rendszer-konvenció: a státusz mindig utolsó, lásd a számla-fület).
      //    A track alsó korlátja fix, mint a számla-listán: a jelvény `whitespace-nowrap`, és tisztán
      //    arányos track-en a hosszabb felirat levágódik.
      id: 'state',
      header: t('invoices.skips.columns.state'),
      cardLabel: t('invoices.skips.columns.state'),
      width: 'minmax(8rem,1fr)',
      multiline: true,
      exportValue: (r) =>
        r.resolvedAt === null
          ? t('invoices.skips.stateOpen')
          : t('invoices.skips.resolvedAt', {
              date: fmtDate(r.resolvedAt, locale),
              runId: r.resolvedRunId ?? '',
            }),
      cell: (r) => {
        const open = r.resolvedAt === null;
        return (
          <div className="min-w-0 space-y-0.5">
            <DomainBadge
              kind="generationSkipState"
              value={open ? 'open' : 'resolved'}
              label={open ? t('invoices.skips.stateOpen') : t('invoices.skips.stateResolved')}
            />
            {!open && r.resolvedAt && (
              <Text tone="tertiary" as="div" className="truncate">
                {t('invoices.skips.resolvedAt', {
                  date: fmtDate(r.resolvedAt, locale),
                  runId: r.resolvedRunId ?? '',
                })}
              </Text>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ListToolbar
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('common.searchPlaceholder'),
        }}
        // A HÓNAP a sáv ELEJÉN, mindig látható vezérlőként (nem a popoverben) — ugyanaz a léptető, amit a
        // számla-fül mutat, és ugyanazt az URL-kulcsot írja: a két fül nem tud külön hónapon állni.
        leading={<MonthStepper month={month} onMonthChange={onMonthChange} />}
        filterItems={[
          {
            kind: 'select',
            key: 'skipReason',
            label: t('invoices.skips.filterReason'),
            value: reason,
            onChange: (v: string) => setFilters({ skipReason: v }),
            options: INVOICE_GENERATION_SKIP_REASONS.map((r) => ({
              value: r,
              label: t(`invoices.generate.skipReason.${r}`, { defaultValue: r }),
            })),
            allValue: ALL,
          },
          ...(canManage
            ? [
                {
                  kind: 'select' as const,
                  key: 'skipClientId',
                  label: t('invoices.skips.filterClient'),
                  value: clientId,
                  onChange: (v: string) => setFilters({ skipClientId: v }),
                  options: clientOptions,
                  allValue: ALL,
                  preserveCase: true,
                },
              ]
            : []),
          {
            kind: 'select',
            key: 'skipState',
            label: t('invoices.skips.filterState'),
            value: state,
            onChange: (v: string) => setFilters({ skipState: v }),
            options: [
              { value: STATE_OPEN, label: t('invoices.skips.filterStateOpen') },
              { value: STATE_RESOLVED, label: t('invoices.skips.filterStateResolved') },
            ],
            // a „mind" (nyitott + lezárt) a lista tetején; az alap `nyitott` is aktív szűrő (Andi #95)
            allValue: STATE_ALL,
          },
        ]}
        meta={
          infinite.isFetching && !infinite.isFetchingNextPage
            ? t('invoices.skips.loading')
            : !isError && !infinite.isLoading
              ? t('invoices.skips.countOf', { shown: rows.length, total })
              : ''
        }
      />
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<SkipRow>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isLoading={infinite.isLoading}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('invoices.skips.noMatches')}
                description={t('invoices.skips.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('invoices.skips.empty')}
                description={t('invoices.skips.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('invoices.skips.openAssignment')}
          actionsColWidth="3.5rem"
          rowActions={(r) => (
            <RowActions>
              {/* FELOLDÁS — csak NYITOTT zár-soron és csak írás-joggal (a zárat a napi generálás olvassa). */}
              {canManage && r.reason === 'manually_removed' && r.resolvedAt === null && (
                <RowActionButton
                  label={t('invoices.skips.release')}
                  icon={LockOpenIcon}
                  onClick={() => release.mutate({ id: r.id })}
                />
              )}
              <RowActionButton
                label={t('invoices.skips.openAssignment')}
                icon={ArrowTopRightOnSquareIcon}
                onClick={() =>
                  void navigate({
                    to: '/services',
                    search: {
                      tab: 'assignments',
                      // A SZERKESZTŐ-SHEET deep-linkje (`asg_edit`), nem csak a sor-kiemelés: a napló-sorról
                      // a kolléga JAVÍTANI megy át (ársor, használat-forrás, kibocsátó) — a lista tetején
                      // landolni és ott újra rákeresni fölösleges kör (user-lelet).
                      asg_edit: r.assignmentId,
                      // ÜGYFÉL-SZŰKÍTÉS is kell: a sheet CSAK akkor nyílik, ha a sor a BETÖLTÖTT lapon van
                      // (`useDeepLinkSheet`), a hozzárendelés-lista viszont 100-asával lapoz, ezer fölötti
                      // halmazon. Az ügyfélre szűkített lista egy lapra fér, tehát a nyitás determinisztikus.
                      asg_client: r.clientId,
                      asg_highlight: r.assignmentId,
                    },
                  })
                }
              />
            </RowActions>
          )}
        />
      </div>
    </>
  );
}
